// Map editor screen (the main menu's «Редактор карт», loaded on first use): palette, inspector, map settings,
// pointer/keyboard input, and the map files — save / open / rename / delete through a MapStore (api.ts),
// export / import as .json, «Играть на этой карте». The model lives in state.ts, the picture in view.ts;
// this file only routes between them and the DOM.
import { newBlankMap } from '../../core/map/blank.js';
import { BUILDING_STYLES, DECOR_KINDS, DEN_KINDS, MAP_SIZE, PROP_KINDS, ROAD_KINDS, WALL_STYLES, type BuildingStyle, type DecorKind, type MapData, type MapIssue, type PropKind, type Pt, type RoadKind, type WallStyle } from '../../core/map/schema.js';
import type { MonsterKind } from '../../core/monsters.js';
import * as S from './state.js';
import { History } from './history.js';
import { createEditorView, sizeHandles } from './view.js';
import type { MapStore } from './api.js';
import { EDITOR_CSS, EDITOR_HTML } from './template.js';
import { oversizeMapFile, parseMapFile } from '../localMaps.js';
import { getLang, t } from '../i18n/index.js';
import { applyI18nAttrs } from '../i18n/dom.js';
import { mapIssueText } from '../i18n/mapIssues.js';
import { monsterName } from '../i18n/names.js';

type Tool = 'select' | S.SpecialKind | S.ObjKey;
const { width: { min: MIN_W, max: MAX_W }, height: { min: MIN_H, max: MAX_H } } = MAP_SIZE;
const SHAPE_TOOLS: Tool[] = ['walls', 'streams', 'roads', 'cliffs', 'ponds'];

/* What the player reads for each stored id (the map files keep the ids), in the current language. */
const propLabel = (k: PropKind) => t(`editor.prop.${k}` as const);
const labelsOf = <T extends string>(ids: readonly T[], label: (id: T) => string) => Object.fromEntries(ids.map(id => [id, label(id)])) as Record<T, string>;
const wallLabels = () => labelsOf(WALL_STYLES, k => t(`editor.wall.${k}` as const));
const buildingLabels = () => labelsOf(BUILDING_STYLES, k => t(`editor.building.${k}` as const));
const decorLabels = () => labelsOf(DECOR_KINDS, k => t(`editor.decor.${k}` as const));
const denLabels = () => labelsOf(DEN_KINDS, (k: MonsterKind) => monsterName(k));
const roadLabels = () => labelsOf(ROAD_KINDS, k => t(`editor.road.${k}` as const));
/* Props share one tool; each kind gets its own palette button that also picks the kind. */
const palette = (): { title: string; tools: { tool: Tool; label: string; key?: string; prop?: PropKind }[] }[] => [
  { title: t('editor.group.edit'), tools: [{ tool: 'select', label: t('editor.tool.select'), key: 'V' }] },
  { title: t('editor.group.required'), tools: [{ tool: 'base', label: t('editor.tool.base') }, { tool: 'side', label: t('editor.tool.side') }, { tool: 'den', label: t('editor.tool.den') }] },
  { title: t('editor.group.points'), tools: [{ tool: 'trees', label: t('editor.tool.trees'), key: 'T' }, { tool: 'rocks', label: t('editor.tool.rocks') }, { tool: 'pillars', label: t('editor.tool.pillars') },
    { tool: 'camps', label: t('editor.tool.camps') }, { tool: 'bridges', label: t('editor.tool.bridges') }] },
  { title: t('editor.group.shapes'), tools: [{ tool: 'buildings', label: t('editor.tool.buildings'), key: 'B' }, { tool: 'brush', label: t('editor.tool.brush') }, { tool: 'decor', label: t('editor.tool.decor') },
    { tool: 'fields', label: t('editor.tool.fields') }, { tool: 'fords', label: t('editor.tool.fords') }] },
  { title: t('editor.group.props'), tools: PROP_KINDS.map(k => ({ tool: 'props' as Tool, label: propLabel(k), prop: k })) },
  { title: t('editor.group.lines'), tools: [{ tool: 'walls', label: t('editor.tool.walls') }, { tool: 'streams', label: t('editor.tool.streams') }, { tool: 'roads', label: t('editor.tool.roads') },
    { tool: 'cliffs', label: t('editor.tool.cliffs') }, { tool: 'ponds', label: t('editor.tool.ponds') }] },
];
const plural = (n: number) => t('editor.problemsCount', { n });
/** A tool's or object kind's name in running text (the palette's own label, a few of them shortened). */
function label(tool: string): string {
  if (tool === 'side') return t('editor.label.side');
  if (tool === 'walls') return t('editor.label.walls');
  if (tool === 'props') return t('editor.label.props');
  return palette().flatMap(g => g.tools).find(x => x.tool === tool)?.label ?? tool;
}

export type EditorDeps = {
  store: MapStore;
  onBack(): void;
  /** Called with a map that passed validation, and its saved name (or its title when unsaved). */
  onPlay(map: MapData, label: string): void;
};
export type EditorHandle = { show(): void; hide(): void };

/** Builds the editor into `root` once; the page keeps it (and its WebGL context) for later visits. Throws
    when WebGL is unavailable. */
export function createMapEditor(root: HTMLElement, deps: EditorDeps): EditorHandle {
  const { store } = deps;
  if (!document.getElementById('map-editor-css')) {
    const style = document.createElement('style');
    style.id = 'map-editor-css';
    style.textContent = EDITOR_CSS;
    document.head.append(style);
  }
  root.classList.add('map-editor');
  root.innerHTML = EDITOR_HTML;
  applyI18nAttrs(root);
  let builtLang = getLang();

  const $ = <T extends HTMLElement = HTMLElement>(id: string) => root.querySelector(`#ed-${id}`) as T;
  const stageEl = $('stage'), canvas = $<HTMLCanvasElement>('view');
  const view = createEditorView(canvas, stageEl);
  let visible = false;

  /* ---- editor state ---- */
  let map: MapData = newBlankMap(120, 80, t('editor.untitled'));
  let file = '';
  let savedJson = '';
  /* The file savedJson was loaded from or saved to: a different File name is unsaved work too. */
  let savedFile = '';
  let knownMaps: string[] = [];
  let issues: MapIssue[] = [];
  let sel: S.Ref | null = null, selVertex = -1, hover: S.Ref | null = null;
  let tool: Tool = 'select';
  let draft: Pt[] = [], draftCursor: Pt | null = null;
  const style: S.PlaceStyle = { wall: 'stone', building: 'house', decor: 'bush', prop: 'haystack', road: 'dirt', width: 3.2, closedCliff: false };
  let snapOn = false, snapStep = 0.5;
  const show = { issues: true };
  const history = new History();
  let coalesce: { key: string; at: number } | null = null;

  const snap = (p: Pt) => S.snapPt(p, snapOn ? snapStep : 0);
  const dirty = () => file !== savedFile || JSON.stringify(map) !== savedJson;
  const isShapeTool = (t: Tool) => SHAPE_TOOLS.includes(t);

  /** Records an undo step (merged with the previous one for repeated wheel/field edits of the same thing). */
  function commit(next: MapData, nextSel: S.Ref | null = sel, coalesceKey?: string) {
    const now = performance.now();
    if (!(coalesceKey && coalesce?.key === coalesceKey && now - coalesce.at < 800)) history.push(map);
    coalesce = coalesceKey ? { key: coalesceKey, at: now } : null;
    map = next;
    sel = nextSel;
    changed();
  }

  const refValid = (ref: S.Ref | null) => !ref || ref.kind !== 'obj' || !!(map[ref.part][ref.key] as unknown[])[ref.index];

  function changed(immediate = false) {
    if (!refValid(hover)) hover = null;
    if (sel?.kind === 'obj' && !(map[sel.part][sel.key] as unknown[])[sel.index]) { sel = null; selVertex = -1; }
    if (sel?.kind === 'obj' && selVertex >= (S.vertices(sel.key, S.getItem(map, sel))?.length ?? 0)) selVertex = -1;
    issues = S.checkMap(map);
    view.setMap(map, immediate);
    view.drawStatic(map);
    renderSettings();
    renderInspector();
    renderIssues();
    renderStatus();
    decorate();
  }

  /** Selection-only change: the map is untouched, so no rebuild or revalidation. */
  function selectionChanged() {
    renderInspector();
    decorate();
  }

  function decorate() {
    view.decorate(map, {
      selected: sel, selectedVertex: selVertex, hover: drag ? null : hover,
      issues: issues.map(i => S.refOfIssue(i.path)).filter((r): r is S.Ref => !!r),
      draft: isShapeTool(tool) && draft.length ? { key: tool as S.ObjKey, pts: draft, cursor: draftCursor, closed: tool === 'ponds' || (tool === 'cliffs' && style.closedCliff) } : null,
      showIssues: show.issues,
    });
    refreshDraftBar();
  }

  /** Minimum points to finish the shape in progress: 3 for a pond or a closed cliff, 2 for any other line. */
  const draftMinPoints = () => tool === 'ponds' || (tool === 'cliffs' && style.closedCliff) ? 3 : 2;

  function refreshDraftBar() {
    const active = isShapeTool(tool) && draft.length > 0;
    $('draftBar').hidden = !active;
    if (active) $<HTMLButtonElement>('draftDone').disabled = draft.length < draftMinPoints();
  }

  function toast(msg: string, ms = 2600) {
    const el = $('toast');
    el.textContent = msg;
    el.hidden = false;
    clearTimeout((toast as unknown as { t?: number }).t);
    (toast as unknown as { t?: number }).t = window.setTimeout(() => { el.hidden = true; }, ms);
  }

  /* ---- palette ---- */
  function renderPalette() {
    const el = $('palette');
    el.innerHTML = '';
    for (const g of palette()) {
      const h = document.createElement('h2');
      h.textContent = g.title;
      const box = document.createElement('div');
      box.className = 'group';
      for (const t of g.tools) {
        const b = document.createElement('button');
        b.className = tool === t.tool && (!t.prop || t.prop === style.prop) ? 'on' : '';
        b.innerHTML = `<span>${t.label}</span>${t.key ? `<kbd>${t.key}</kbd>` : ''}`;
        b.onclick = () => { if (t.prop) style.prop = t.prop; setTool(t.tool); };
        box.append(b);
      }
      el.append(h, box);
    }
    const opts = toolOptions();
    if (opts) {
      const h = document.createElement('h2');
      h.textContent = t('editor.toolSettings');
      el.append(h, opts);
    }
  }

  function selectEl<T extends string>(options: readonly T[], value: T, onChange: (v: T) => void, labels?: Record<T, string>) {
    const s = document.createElement('select');
    for (const o of options) s.add(new Option(labels?.[o] ?? o, o, false, o === value));
    s.onchange = () => onChange(s.value as T);
    return s;
  }
  function numberEl(value: number, step: number, onChange: (v: number) => void, min?: number) {
    const i = document.createElement('input');
    i.type = 'number'; i.step = String(step); i.value = String(+value.toFixed(3));
    if (min !== undefined) i.min = String(min);
    i.onchange = () => { const v = Number(i.value); if (Number.isFinite(v) && (min === undefined || v >= min)) onChange(v); else i.value = String(value); };
    return i;
  }
  function row(label: string, control: HTMLElement) {
    const r = document.createElement('div');
    r.className = 'row';
    const l = document.createElement('label');
    l.textContent = label;
    r.append(l, control);
    return r;
  }

  function toolOptions(): HTMLElement | null {
    const box = document.createElement('div');
    if (tool === 'walls') box.append(row(t('editor.style'), selectEl(WALL_STYLES, style.wall, v => { style.wall = v; }, wallLabels())));
    else if (tool === 'buildings') box.append(row(t('editor.style'), selectEl(BUILDING_STYLES, style.building, v => { style.building = v; }, buildingLabels())));
    else if (tool === 'decor') box.append(row(t('editor.type'), selectEl(DECOR_KINDS, style.decor, v => { style.decor = v; }, decorLabels())));
    else if (tool === 'streams' || tool === 'roads') {
      if (tool === 'roads') box.append(row(t('editor.type'), selectEl(ROAD_KINDS, style.road, v => { style.road = v; }, roadLabels())));
      box.append(row(t('editor.width'), numberEl(style.width, 0.1, v => { style.width = v; }, 0.1)));
    } else if (tool === 'cliffs') {
      const c = document.createElement('input');
      c.type = 'checkbox'; c.checked = style.closedCliff;
      c.onchange = () => { style.closedCliff = c.checked; decorate(); };
      box.append(row(t('editor.closed'), c));
    } else return null;
    return box;
  }

  function setTool(t: Tool) {
    if (draft.length) draft = [];
    tool = t;
    stageEl.dataset.tool = t;
    renderPalette();
    renderHint();
    decorate();
  }

  function renderHint() {
    const hints: Partial<Record<Tool, string>> = {
      select: t('editor.hint.select'),
      base: t('editor.hint.base'),
      side: t('editor.hint.side'),
      den: t('editor.hint.den'),
      bridges: t('editor.hint.bridges'),
      fords: t('editor.hint.fords'),
      props: t('editor.hint.props', { name: propLabel(style.prop) }),
    };
    $('hint').textContent = hints[tool] ?? (isShapeTool(tool)
      ? t('editor.hint.shape', { name: label(tool) })
      : t('editor.hint.place', { name: label(tool) }));
  }

  /* ---- map settings ---- */
  function renderSettings() {
    const set = (id: string, v: string) => { const el = $<HTMLInputElement>(id); if (document.activeElement !== el) el.value = v; };
    set('mapName', map.name);
    set('width', String(map.width));
    set('height', String(map.height));
    set('pointRadius', String(map.pointRadius));
    $('docName').textContent = file ? store.label(file) : t('editor.notSaved');
  }

  $<HTMLInputElement>('mapName').onchange = e => {
    const v = (e.target as HTMLInputElement).value.trim();
    if (v) commit({ ...map, name: v }); else renderSettings();
  };
  for (const [id, field, min, max] of [['width', 'width', MIN_W, MAX_W], ['height', 'height', MIN_H, MAX_H], ['pointRadius', 'pointRadius', 1, 20]] as const) {
    $<HTMLInputElement>(id).onchange = e => {
      const v = Number((e.target as HTMLInputElement).value);
      if (!Number.isFinite(v)) return renderSettings();
      if (v < min || v > max) toast(t('editor.outOfRange', { field, min, max }));
      commit({ ...map, [field]: Math.min(max, Math.max(min, v)) });
    };
  }

  /* ---- inspector ---- */
  const deg = (r: number) => +(r * 180 / Math.PI).toFixed(2);
  const rad = (d: number) => Math.round(d * Math.PI / 180 * 1e4) / 1e4;

  function editItem(ref: S.ObjRef, patch: Record<string, unknown>, key: string) {
    const r = S.setItem(map, ref, { ...(S.getItem(map, ref) as object), ...patch } as S.Item);
    commit(r.map, r.ref, key);
  }

  function renderInspector() {
    const el = $('inspector');
    el.innerHTML = '';
    if (!sel) {
      el.innerHTML = `<div style="color:var(--muted)">${t('editor.nothingSelected')}</div>`;
      return;
    }
    const title = document.createElement('div');
    title.className = 'kind';
    if (sel.kind !== 'obj') {
      const kind = sel.kind;
      title.innerHTML = `${label(kind)}<small>${kind === 'base' ? t('editor.setsDiagonal') : t('editor.mirrored')}</small>`;
      el.append(title);
      const p = S.specialPos(map, kind);
      el.append(row('x, z', pairEl(p, v => commit(S.setSpecial(map, kind, v), sel, `special:${kind}`))));
      if (kind === 'den') el.append(row(t('editor.type'), selectEl(DEN_KINDS, map.den.kind, v => commit({ ...map, den: { ...map.den, kind: v } }), denLabels())));
      return;
    }
    const ref = sel, item = S.getItem(map, ref), o = item as unknown as Record<string, number | string | boolean>;
    title.innerHTML = `${ref.key === 'props' ? propLabel(o.kind as PropKind) : label(ref.key)}<small>${ref.part === 'center' ? t('editor.partCenter') : t('editor.partHalf')} #${ref.index}</small>`;
    el.append(title);
    const pts = S.vertices(ref.key, item);
    if (!pts) {
      el.append(row('x, z', pairEl(S.anchor(ref.key, item), v => { const r = S.setItem(map, ref, S.moveTo(ref.key, item, v)); commit(r.map, r.ref, 'pos'); })));
    }
    const num = (label: string, field: string, step: number, min?: number) =>
      el.append(row(label, numberEl(o[field] as number, step, v => editItem(ref, { [field]: v }, `f:${field}`), min)));
    if (ref.key === 'buildings' || ref.key === 'fields' || ref.key === 'fords') { num(t('editor.w'), 'w', 0.5, 0.1); num(t('editor.d'), 'd', 0.5, 0.1); }
    if (ref.key === 'brush') { num(t('editor.rx'), 'rx', 0.25, 0.1); num(t('editor.rz'), 'rz', 0.25, 0.1); }
    if (ref.key === 'decor') num(t('editor.scale'), 'scale', 0.1, 0.1);
    if (ref.key === 'streams' || ref.key === 'roads') num(t('editor.width'), 'width', 0.1, 0.1);
    if ('rot' in o) el.append(row(t('editor.rotation'), numberEl(deg(o.rot as number), 5, v => editItem(ref, { rot: rad(v) }, 'f:rot'))));
    if (ref.key === 'buildings') el.append(row(t('editor.style'), selectEl(BUILDING_STYLES, o.style as BuildingStyle, v => editItem(ref, { style: v }, 'f:style'), buildingLabels())));
    if (ref.key === 'walls') el.append(row(t('editor.style'), selectEl(WALL_STYLES, o.style as WallStyle, v => editItem(ref, { style: v }, 'f:style'), wallLabels())));
    if (ref.key === 'decor') el.append(row(t('editor.type'), selectEl(DECOR_KINDS, o.kind as DecorKind, v => editItem(ref, { kind: v }, 'f:kind'), decorLabels())));
    if (ref.key === 'props') el.append(row(t('editor.type'), selectEl(PROP_KINDS, o.kind as PropKind, v => editItem(ref, { kind: v }, 'f:kind'), labelsOf(PROP_KINDS, propLabel))));
    if (ref.key === 'roads') el.append(row(t('editor.type'), selectEl(ROAD_KINDS, (o.kind as RoadKind | undefined) ?? 'dirt', v => editItem(ref, { kind: v }, 'f:kind'), roadLabels())));
    if (ref.key === 'cliffs') {
      const c = document.createElement('input');
      c.type = 'checkbox'; c.checked = !!o.closed; c.disabled = !o.closed && pts!.length < 3;
      c.onchange = () => editItem(ref, { closed: c.checked }, 'f:closed');
      el.append(row(t('editor.closed'), c));
    }
    if (pts) {
      const info = document.createElement('div');
      info.style.cssText = 'color:var(--muted);font-size:12.5px;margin:4px 0';
      info.textContent = t('editor.vertices', { n: pts.length, selected: selVertex >= 0 ? t('editor.vertexSelected', { i: String(selVertex) }) : '' });
      el.append(info);
      if (selVertex >= 0) {
        el.append(row(t('editor.vertex', { i: String(selVertex) }), pairEl(pts[selVertex]!, v => { const r = S.moveVertex(map, ref, selVertex, v); commit(r.map, r.ref, 'vertex'); })));
      }
    }
    const buttons = document.createElement('div');
    buttons.className = 'buttons';
    const btn = (label: string, f: () => void, title = '') => { const b = document.createElement('button'); b.textContent = label; b.title = title; b.onclick = f; buttons.append(b); };
    btn(t('editor.duplicate'), duplicateSel, 'Ctrl+D');
    btn(t('editor.delete'), deleteSel, 'Del');
    if (S.canToggleCenter(map, ref)) {
      btn(ref.part === 'center' ? t('editor.toHalf') : t('editor.toCenter'), () => {
        const r = S.toggleCenter(map, ref);
        commit(r.map, r.ref);
      }, t('editor.centerTitle'));
    }
    el.append(buttons);
  }

  function pairEl([x, z]: Pt, onChange: (p: Pt) => void) {
    const box = document.createElement('div');
    box.className = 'pair';
    const xi = numberEl(x, 0.5, v => onChange([v, z])), zi = numberEl(z, 0.5, v => onChange([x, v]));
    box.append(xi, zi);
    return box;
  }

  /* ---- problems list ---- */
  /** The open map's problems, or (with `heading`) another file's — those point at nothing on this map. */
  function renderIssues(list = issues, heading?: string) {
    const ul = $('issues');
    ul.innerHTML = '';
    if (heading) { const li = document.createElement('li'); li.className = 'head'; li.textContent = heading; ul.append(li); }
    if (!list.length) {
      ul.innerHTML = `<li class="none">${t('editor.noProblems')}</li>`;
      return;
    }
    for (const issue of list.slice(0, 60)) {
      const li = document.createElement('li');
      const b = document.createElement('b');
      b.textContent = issue.path || t('editor.issueRoot');
      li.append(b, ' ', mapIssueText(issue));
      const ref = heading ? null : S.refOfIssue(issue.path);
      if (ref) li.onclick = () => { sel = ref; selVertex = -1; selectionChanged(); };
      ul.append(li);
    }
    if (list.length > 60) { const li = document.createElement('li'); li.textContent = t('editor.more', { n: list.length - 60 }); ul.append(li); }
  }

  let rebuildInfo = '';
  view.onRebuilt = (ms, err) => {
    rebuildInfo = err ? t('editor.renderError', { error: String(err instanceof Error ? err.message : err) }) : t('editor.rebuilt', { ms: ms.toFixed(0) });
    renderStatus();
  };
  let cursorAt: Pt | null = null;
  function renderStatus() {
    const d = dirty();
    $('status').innerHTML = '';
    const parts: [string, string?][] = [
      [`${file ? store.label(file) : t('editor.unsavedParen')}${d ? t('editor.changed') : ''}`, d ? 'dirty' : undefined],
      [t('editor.sizeStatus', { w: String(map.width), h: String(map.height) })],
      [cursorAt ? `x ${cursorAt[0].toFixed(2)}  z ${cursorAt[1].toFixed(2)}` : ''],
      [snapOn ? t('editor.snapOn', { step: String(snapStep) }) : t('editor.snapOff')],
      [issues.length ? plural(issues.length) : t('editor.noErrors')],
      [rebuildInfo],
    ];
    for (const [text, cls] of parts) {
      const s = document.createElement('span');
      s.textContent = text;
      if (cls) s.className = cls;
      $('status').append(s);
    }
    if (visible) document.title = t('editor.docTitle', { mark: d ? '• ' : '', name: file || t('editor.newMap') });
  }

  /* ---- edits on the selection ---- */
  function deleteSel() {
    if (!sel) return;
    if (sel.kind !== 'obj') { toast(t('editor.requiredNoDelete')); return; }
    if (selVertex >= 0) {
      const r = S.deleteVertex(map, sel, selVertex);
      if (!r) { toast(t('editor.minVertices')); return; }
      selVertex = -1;
      commit(r.map, r.ref);
      return;
    }
    commit(S.deleteItem(map, sel), null);
  }
  function duplicateSel() {
    if (sel?.kind !== 'obj') return;
    const r = S.duplicateItem(map, sel);
    selVertex = -1;
    commit(r.map, r.ref);
  }
  function rotateSel(da: number, key = 'rotate') {
    if (sel?.kind !== 'obj') return;
    const r = S.setItem(map, sel, S.rotate(sel.key, S.getItem(map, sel), da));
    commit(r.map, r.ref, key);
  }
  function resizeSel(k: number) {
    if (sel?.kind !== 'obj') return;
    const r = S.setItem(map, sel, S.resize(sel.key, S.getItem(map, sel), k));
    commit(r.map, r.ref, 'resize');
  }

  function finishDraft() {
    if (!isShapeTool(tool)) return;
    const item = S.newShape(tool as S.ObjKey, draft, style);
    if (!item) { toast(t('editor.needPoints', { n: draftMinPoints() })); return; }
    const r = S.addItem(map, tool as S.ObjKey, item);
    draft = []; draftCursor = null;
    selVertex = -1;
    commit(r.map, r.ref);
  }

  /* ---- pointer input ---- */
  /* An edit drag starts only past this many pixels: a plain click must not nudge (or re-round) what it selects. */
  const DRAG_SLOP = 3;
  type EditDrag = { start: MapData; sx: number; sy: number; moved: boolean };
  type Drag =
    | { kind: 'pan'; x: number; y: number; moved: boolean }
    | EditDrag & { kind: 'move'; ref: S.Ref; from: Pt; anchor: Pt }
    | EditDrag & { kind: 'vertex'; ref: S.ObjRef; index: number }
    | EditDrag & { kind: 'size'; ref: S.ObjRef; field: 'w' | 'd' | 'rx' | 'rz' };
  let drag: Drag | null = null;

  const refAnchor = (ref: S.Ref): Pt => ref.kind === 'obj' ? S.anchor(ref.key, S.getItem(map, ref)) : S.specialPos(map, ref.kind);

  canvas.addEventListener('contextmenu', e => e.preventDefault());
  canvas.addEventListener('pointerdown', e => {
    canvas.setPointerCapture(e.pointerId);
    const p = view.screenToWorld(e.clientX, e.clientY), mpp = view.metresPerPixel();
    if (e.button !== 0) { drag = { kind: 'pan', x: e.clientX, y: e.clientY, moved: false }; stageEl.classList.add('panning'); return; }

    if (tool === 'select') {
      if (sel?.kind === 'obj') {
        const ref = sel, item = S.getItem(map, ref);
        const vi = S.pickVertex(map, ref, p, 8 * mpp);
        if (vi >= 0) {
          if (e.altKey) { selVertex = vi; deleteSel(); return; }
          selVertex = vi;
          drag = { kind: 'vertex', start: map, sx: e.clientX, sy: e.clientY, ref, index: vi, moved: false };
          selectionChanged();
          return;
        }
        const h = sizeHandles(ref.key, item).find(h => Math.hypot(h.pos[0] - p[0], h.pos[1] - p[1]) <= 8 * mpp);
        if (h) { drag = { kind: 'size', start: map, sx: e.clientX, sy: e.clientY, ref, field: h.field, moved: false }; return; }
        if (S.hasVertices(ref.key) && (e.altKey || e.detail === 2)) {
          const seg = S.pickSegment(map, ref, p, 8 * mpp);
          if (seg >= 0) {
            const r = S.insertVertex(map, ref, seg, snap(p));
            selVertex = seg + 1;
            commit(r.map, r.ref);
            return;
          }
        }
      }
      const ref = S.pick(map, p, 6 * mpp);
      if (!ref) {
        sel = null; selVertex = -1;
        drag = { kind: 'pan', x: e.clientX, y: e.clientY, moved: false };
        stageEl.classList.add('panning');
        selectionChanged();
        return;
      }
      if (!S.sameRef(ref, sel)) selVertex = -1;
      sel = ref;
      drag = { kind: 'move', start: map, sx: e.clientX, sy: e.clientY, ref, from: p, anchor: refAnchor(ref), moved: false };
      selectionChanged();
      return;
    }

    if (tool === 'base' || tool === 'side' || tool === 'den') {
      commit(S.placeSpecial(map, tool, snap(p)), { kind: tool });
      return;
    }
    if (isShapeTool(tool)) {
      if (e.detail >= 2) { finishDraft(); return; }
      const q = snap(p), last = draft[draft.length - 1];
      if (!last || last[0] !== q[0] || last[1] !== q[1]) draft.push(q);
      decorate();
      return;
    }
    const item = tool === 'fords' ? S.newFord(map, snap(p)) : S.newItem(tool, snap(p), style);
    if (!item) { if (tool === 'fords') toast(t('editor.fordNeedsStream')); return; }
    const r = S.addItem(map, tool, item);
    selVertex = -1;
    commit(r.map, r.ref);
  });

  canvas.addEventListener('pointermove', e => {
    const p = view.screenToWorld(e.clientX, e.clientY);
    cursorAt = p;
    if (!drag) {
      if (isShapeTool(tool)) { draftCursor = snap(p); if (draft.length) decorate(); }
      if (tool === 'select') {
        const h = S.pick(map, p, 6 * view.metresPerPixel());
        if (!S.sameRef(h, hover) && (h || hover)) { hover = h; decorate(); }
      }
      renderStatus();
      return;
    }
    if (drag.kind === 'pan') {
      view.pan(e.clientX - drag.x, e.clientY - drag.y);
      drag.x = e.clientX; drag.y = e.clientY; drag.moved = true;
      decorate();
      return;
    }
    if (!drag.moved && Math.hypot(e.clientX - drag.sx, e.clientY - drag.sy) < DRAG_SLOP) return;
    drag.moved = true;
    if (drag.kind === 'move') {
      const target = snap([drag.anchor[0] + p[0] - drag.from[0], drag.anchor[1] + p[1] - drag.from[1]]);
      if (drag.ref.kind !== 'obj') {
        map = S.setSpecial(drag.start, drag.ref.kind, target);
      } else {
        const r = S.setItem(drag.start, drag.ref, S.moveTo(drag.ref.key, S.getItem(drag.start, drag.ref), target));
        map = r.map; sel = r.ref;
      }
    } else if (drag.kind === 'vertex') {
      const r = S.moveVertex(drag.start, drag.ref, drag.index, snap(p));
      map = r.map; sel = r.ref;
    } else {
      const item = S.getItem(drag.start, drag.ref) as unknown as { x: number; z: number; rot: number };
      const c = Math.cos(item.rot), s = Math.sin(item.rot), dx = p[0] - item.x, dz = p[1] - item.z;
      const along = drag.field === 'w' || drag.field === 'rx' ? dx * c + dz * s : -dx * s + dz * c;
      const size = Math.max(0.2, Math.round(Math.abs(along) * (drag.field === 'w' || drag.field === 'd' ? 2 : 1) * 100) / 100);
      const r = S.setItem(drag.start, drag.ref, { ...item, [drag.field]: snapOn ? Math.max(snapStep, Math.round(size / snapStep) * snapStep) : size } as unknown as S.Item);
      map = r.map; sel = r.ref;
    }
    changed();
  });

  function endDrag() {
    if (!drag) return;
    const edited = drag.kind !== 'pan' && drag.moved;
    if (edited && drag.kind !== 'pan') { history.push(drag.start); coalesce = null; }
    drag = null;
    stageEl.classList.remove('panning');
    if (edited) changed(); else selectionChanged();
  }
  canvas.addEventListener('pointerup', endDrag);
  canvas.addEventListener('pointercancel', endDrag);
  canvas.addEventListener('pointerleave', () => { cursorAt = null; if (hover) { hover = null; decorate(); } renderStatus(); });

  canvas.addEventListener('wheel', e => {
    e.preventDefault();
    if (e.altKey && sel?.kind === 'obj') {
      rotateSel(Math.sign(e.deltaY || e.deltaX) * Math.PI / 36, 'wheel-rotate');
      return;
    }
    view.zoomAt(e.clientX, e.clientY, Math.exp(e.deltaY * 0.0015));
    decorate();
  }, { passive: false });

  /* ---- keyboard ---- */
  window.addEventListener('keydown', e => {
    if (!visible) return;
    const target = e.target as HTMLElement;
    if (target.matches('input, select, textarea')) {
      if (e.key === 'Escape') target.blur();
      return;
    }
    const mod = e.ctrlKey || e.metaKey, k = e.key.toLowerCase();
    if (mod && k === 'z') { e.preventDefault(); e.shiftKey ? redo() : undo(); return; }
    if (mod && k === 'y') { e.preventDefault(); redo(); return; }
    if (mod && k === 'd') { e.preventDefault(); duplicateSel(); return; }
    if (mod && k === 's') { e.preventDefault(); void save(); return; }
    if (mod) return;
    if (e.key === 'Enter') { finishDraft(); return; }
    if (e.key === 'Escape') {
      e.preventDefault();
      if (!$('help').hidden) $('help').hidden = true;
      else if (draft.length) { draft = []; decorate(); } else if (tool !== 'select') setTool('select');
      else if (selVertex >= 0) { selVertex = -1; selectionChanged(); } else if (sel) { sel = null; selectionChanged(); }
      else leave();
      return;
    }
    if (e.key === 'Backspace' && draft.length) { e.preventDefault(); draft.pop(); decorate(); return; }
    if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); deleteSel(); return; }
    if (k === 'r') { rotateSel((e.shiftKey ? -1 : 1) * Math.PI / 12); return; }
    if (e.key === '[') { resizeSel(1 / 1.1); return; }
    if (e.key === ']') { resizeSel(1.1); return; }
    if (k === 'v') { setTool('select'); return; }
    if (k === 't') { setTool('trees'); return; }
    if (k === 'b') { setTool('buildings'); return; }
    if (k === 'g') { snapOn = !snapOn; $<HTMLInputElement>('snap').checked = snapOn; renderStatus(); return; }
    if (k === 'f') { view.frame(map); decorate(); return; }
    if (e.key === '?' || k === 'h') { $('help').hidden = !$('help').hidden; }
  });

  function undo() {
    const prev = history.undo(map);
    if (!prev) { toast(t('editor.nothingToUndo')); return; }
    map = prev; coalesce = null; changed();
  }
  function redo() {
    const next = history.redo(map);
    if (!next) { toast(t('editor.nothingToRedo')); return; }
    map = next; coalesce = null; changed();
  }

  $('draftDone').onclick = () => finishDraft();
  $('draftCancel').onclick = () => { draft = []; draftCursor = null; decorate(); };

  /* ---- view toggles ---- */
  $<HTMLInputElement>('snap').onchange = e => { snapOn = (e.target as HTMLInputElement).checked; renderStatus(); };
  $<HTMLInputElement>('snapStep').onchange = e => { const v = Number((e.target as HTMLInputElement).value); if (v > 0) snapStep = v; renderStatus(); };
  $<HTMLInputElement>('ovIssues').onchange = e => { show.issues = (e.target as HTMLInputElement).checked; decorate(); };
  $<HTMLInputElement>('ovNav').onchange = e => view.setOverlay('nav', (e.target as HTMLInputElement).checked);
  $<HTMLInputElement>('ovSight').onchange = e => view.setOverlay('sight', (e.target as HTMLInputElement).checked);

  /* ---- files ---- */
  function setDocument(next: MapData, name: string, saved = true) {
    map = next; file = name; savedFile = name; savedJson = saved ? JSON.stringify(next) : '';
    sel = null; selVertex = -1; draft = [];
    history.clear(); coalesce = null;
    changed(true);
    view.frame(map);
    rememberLast(name);
  }

  async function refreshList(selected?: string) {
    const r = await store.list();
    const s = $<HTMLSelectElement>('open');
    s.innerHTML = '';
    if (!r.ok) { toast(mapIssueText(r.issues[0]!)); return null; }
    knownMaps = r.value.maps;
    if (!knownMaps.length) s.add(new Option(t('editor.noneYet'), ''));
    for (const m of knownMaps) s.add(new Option(m === r.value.active ? t('editor.activeMap', { name: m }) : m, m, false, m === (selected ?? file)));
    for (const id of ['load', 'rename', 'remove']) $<HTMLButtonElement>(id).disabled = !knownMaps.length;
    return r.value;
  }

  const confirmDiscard = () => !dirty() || confirm(t('editor.confirmDiscard'));
  /** One problem with no path (storage full, server down) is shown as is; map problems go to «Проблемы». */
  const failure = (list: MapIssue[]) => list.length === 1 && !list[0]!.path ? mapIssueText(list[0]!) : t('editor.seeProblems', { problems: plural(list.length) });

  async function open(name: string): Promise<boolean> {
    const r = await store.load(name);
    if (!r.ok) { toast(t('editor.openFailed', { name: store.label(name), reason: mapIssueText(r.issues[0]!) }), 4000); return false; }
    setDocument(r.value, name);
    toast(t('editor.opened', { name: store.label(name) }));
    return true;
  }

  async function saveAs(name: string): Promise<boolean> {
    const bad = store.checkName(name);
    if (bad) { toast(t('editor.notSavedReason', { reason: mapIssueText(bad) }), 4000); return false; }
    if (name !== savedFile && knownMaps.includes(name) && !confirm(t('editor.confirmOverwrite', { name: store.label(name) }))) return false;
    const r = await store.save(name, map);
    if (!r.ok) {
      if (r.issues.some(i => i.path)) {
        issues = r.issues;
        renderIssues();
        const first = r.issues.map(i => S.refOfIssue(i.path)).find(Boolean);
        if (first) { sel = first; selVertex = -1; renderInspector(); }
        decorate();
      }
      toast(t('editor.notSavedReason', { reason: failure(r.issues) }), 5000);
      return false;
    }
    file = name;
    savedJson = JSON.stringify(map);
    savedFile = name;
    rememberLast(name);
    renderSettings();
    renderStatus();
    await refreshList(name);
    toast(t('editor.saved', { name: store.label(name) }));
    return true;
  }
  async function saveAsPrompt(): Promise<boolean> {
    const name = prompt(store.kind === 'dev' ? t('editor.promptFile') : t('editor.promptName'), file || (store.kind === 'dev' ? '' : map.name));
    return name === null ? false : saveAs(name.trim());
  }
  const save = () => file ? saveAs(file) : saveAsPrompt();

  /* The last map this browser worked on reopens with the editor (after a match the page reloads). */
  const LAST_KEY = 'bestiarum-editor-last';
  function rememberLast(name: string) {
    if (store.kind !== 'local') return;
    try { if (name) localStorage.setItem(LAST_KEY, name); else localStorage.removeItem(LAST_KEY); } catch { /* storage blocked */ }
  }
  function lastOpened(): string | null {
    try { return localStorage.getItem(LAST_KEY); } catch { return null; }
  }

  /* The work stays in memory while the page lives, but a match's end reloads the page. */
  function leave() {
    if (dirty() && !confirm(t('editor.confirmLeave'))) return;
    deps.onBack();
  }

  async function play() {
    if (issues.length) {
      toast(t('editor.cannotPlay', { problems: plural(issues.length) }), 5000);
      return;
    }
    if (dirty() && !(await save())) return;
    deps.onPlay(map, file || map.name);
  }

  function exportFile() {
    if (issues.length && !confirm(t('editor.confirmExport', { problems: plural(issues.length) }))) return;
    const slug = (file || map.name).trim().replace(/[^\p{L}\p{N}_-]+/gu, '-').replace(/^-+|-+$/g, '') || 'map';
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([`${JSON.stringify(map, null, 2)}\n`], { type: 'application/json' }));
    a.download = `${slug}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
  }

  async function importFile(f: File) {
    const r = oversizeMapFile(f.size) ?? parseMapFile(await f.text());  // a huge file is refused unread
    if (!r.ok) {
      renderIssues(r.issues, t('editor.importFailedHead', { file: f.name }));
      toast(t('editor.importFailed', { reason: failure(r.issues) }), 5000);
      return;
    }
    if (!confirmDiscard()) return;
    setDocument(r.value, '', false);
    toast(t('editor.imported', { file: f.name }), 4000);
  }

  $('save').onclick = () => void save();
  $('saveAs').onclick = () => void saveAsPrompt();
  $('play').onclick = () => void play();
  $('back').onclick = leave;
  $('helpBtn').onclick = () => { $('help').hidden = !$('help').hidden; };
  $('export').onclick = exportFile;
  $('import').onclick = () => $<HTMLInputElement>('importFile').click();
  $<HTMLInputElement>('importFile').onchange = e => {
    const input = e.target as HTMLInputElement, f = input.files?.[0];
    input.value = '';  // the same file picked again must fire change again
    if (f) void importFile(f);
  };
  $('load').onclick = () => {
    const name = $<HTMLSelectElement>('open').value;
    if (name && confirmDiscard()) void open(name);
  };
  $('rename').onclick = async () => {
    const from = $<HTMLSelectElement>('open').value;
    if (!from || !store.rename) return;
    const to = prompt(t('editor.promptRename', { name: store.label(from) }), from)?.trim();
    if (!to || to === from) return;
    const r = await store.rename(from, to);
    if (!r.ok) { toast(t('editor.renameFailed', { reason: mapIssueText(r.issues[0]!) }), 4000); return; }
    if (file === from) { file = to; savedFile = to; rememberLast(to); renderSettings(); renderStatus(); }
    await refreshList(to);
    toast(t('editor.renamed', { name: store.label(to) }));
  };
  $('remove').onclick = async () => {
    const name = $<HTMLSelectElement>('open').value;
    if (!name || !store.remove || !confirm(t('editor.confirmRemove', { name: store.label(name) }))) return;
    const r = await store.remove(name);
    if (!r.ok) { toast(t('editor.removeFailed', { reason: mapIssueText(r.issues[0]!) }), 4000); return; }
    // the open copy stays, now as unsaved work
    if (file === name) { file = ''; savedFile = ''; savedJson = ''; rememberLast(''); renderSettings(); renderStatus(); }
    await refreshList();
    toast(t('editor.removed', { name: store.label(name) }));
  };
  $('loadTemplate').onclick = () => {
    const name = $<HTMLSelectElement>('template').value;
    if (!name || !confirmDiscard()) return;
    const r = store.loadTemplate(name);
    if (!r.ok) { toast(t('editor.templateFailed', { reason: mapIssueText(r.issues[0]!) }), 4000); return; }
    setDocument(r.value, '');
    toast(t('editor.templateOpened'), 4000);
  };
  $('newBlank').onclick = () => {
    const w = Math.min(MAX_W, Math.max(MIN_W, Number($<HTMLInputElement>('width').value) || 120)), h = Math.min(MAX_H, Math.max(MIN_H, Number($<HTMLInputElement>('height').value) || 80));
    if (!confirm(t('editor.confirmNew', { w: String(w), h: String(h), lost: dirty() ? t('editor.changesLost') : '' }))) return;
    setDocument(newBlankMap(w, h, t('editor.untitled')), '');
  };
  if (store.makeActive) {
    const makeActive = store.makeActive;
    $('makeActive').hidden = false;
    $('makeActive').onclick = async () => {
      if (dirty() && !(await save())) return;
      if (!file) return;
      const r = await makeActive(file);
      if (!r.ok) { toast(t('editor.activeFailed', { reason: mapIssueText(r.issues[0]!) }), 4000); return; }
      await refreshList(file);
      toast(t('editor.nowActive', { name: store.label(file) }), 5000);
    };
  }
  const renderMineTitle = () => { $('mineTitle').textContent = store.kind === 'dev' ? t('editor.devMaps') : t('maps.mine'); };
  renderMineTitle();
  $('mineButtons').hidden = !store.rename;
  $('templatesBox').hidden = !store.templates.length;
  for (const t of store.templates) $<HTMLSelectElement>('template').add(new Option(t, t));
  // only while the editor shows: a reload from elsewhere (a match's «В меню») already passed leave()'s question
  window.addEventListener('beforeunload', e => { if (visible && dirty()) e.preventDefault(); });

  /* Console handle for automated checks. */
  (window as unknown as { editor: unknown }).editor = { get map() { return map; }, get sel() { return sel; }, view, S, open, save: saveAs, setTool, setDocument };

  renderPalette();
  renderHint();
  let started = false, pageTitle = document.title;
  async function start() {
    const list = await refreshList();
    const initial = store.kind === 'dev' ? list?.active : lastOpened();
    if (!(initial && list?.maps.includes(initial) && await open(initial))) setDocument(map, '');
  }

  return {
    show() {
      pageTitle = document.title;
      visible = true;
      root.hidden = false;
      if (builtLang !== getLang()) {  // the language changed in the settings since the editor was last shown
        builtLang = getLang();
        applyI18nAttrs(root);
        renderMineTitle();
        renderPalette();
        renderHint();
        renderSettings();
        renderInspector();
        renderIssues();
        void refreshList();
      }
      renderStatus();
      if (!started) { started = true; void start(); } else view.requestDraw();
    },
    hide() {
      visible = false;
      root.hidden = true;
      document.title = pageTitle;
    },
  };
}
